// ReviewChip — chip surface for web-search sources that ground an
// assistant answer. Sits below the assistant turn alongside
// ProjectChipRow.
//
// The row collapses by default to a single "N sources" pill with up to
// three stacked source avatars (favicon when the backend provides one,
// emoji fallback otherwise). Click expands inline into the full
// flex-wrap of per-source chips; click "Hide" to recollapse. This
// keeps the resting state quiet — a one-line citation footer — while
// preserving full per-source clickthrough on demand.
//
// Why collapse-first instead of always-expanded: 8 wide pills wrapping
// to 4 rows visually outweighs the prose above. The "sources" surface
// is supporting metadata; the prose is the answer. Pattern mirrors
// citation-stacks used by other AI products (ChatGPT, Perplexity).

import { useState } from "react";
import type { ReviewRef } from "@/lib/review-refs";

const AVATAR_PREVIEW_COUNT = 3;
const TITLE_TRUNCATE = 26;

interface ReviewChipProps {
  refData: ReviewRef;
}

function isRedditSource(source: string, backend?: string): boolean {
  // Catch both reddit.com hosts and the "reddit.com/r/<sub>" sub-hosted
  // label our backend emits. `backend` is the authoritative signal but
  // we fall back to the source string for hydration paths where the
  // backend field was stripped by an older serializer.
  if (backend === "reddit") return true;
  return /\breddit\.com\b/i.test(source);
}

function chipAccent(source: string, backend?: string): {
  ring: string;
  text: string;
  bg: string;
  emoji: string;
} {
  if (isRedditSource(source, backend)) {
    return {
      ring: "rgba(255,122,89,0.35)",
      text: "#ff7a59",
      bg: "rgba(255,122,89,0.07)",
      emoji: "💬",
    };
  }
  return {
    ring: "rgba(96,165,250,0.30)",
    text: "var(--color-text-sec)",
    bg: "var(--color-surface-2)",
    emoji: "🔗",
  };
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + "…";
}

// hostFromRef extracts a registrable host from a ReviewRef. `source`
// is the backend's preferred label (often "reddit.com/r/<sub>"); we
// keep only the host segment. Falls back to parsing `url` when source
// is empty — defensive for hydrated rows from older data.
function hostFromRef(refData: ReviewRef): string {
  const src = (refData.source || "").trim();
  if (src) {
    const head = src.split("/")[0]?.trim();
    if (head) return head.toLowerCase();
  }
  try {
    return new URL(refData.url).hostname.toLowerCase();
  } catch {
    return "";
  }
}

// resolveFavicon picks the best favicon URL we can render for this ref.
// Brave's CDN URL (set by the new web_search path) is preferred — it's
// the same image Brave's own UI uses, already sized 32×32. For rows
// from before favicon plumbing landed (or any backend that doesn't
// supply one), fall back to DuckDuckGo's icons.duckduckgo.com service
// which serves real PNGs for any host, free, no auth.
function resolveFavicon(refData: ReviewRef): string | undefined {
  if (refData.favicon) return refData.favicon;
  const host = hostFromRef(refData);
  if (!host) return undefined;
  return `https://icons.duckduckgo.com/ip3/${host}.ico`;
}

// SourceAvatar renders a single-source dot — favicon when the search
// backend supplied one, otherwise the accent-coloured emoji. Sized to
// match neighbouring chip text height; intentionally not a button (the
// click target is the wrapping summary or chip).
function SourceAvatar({ refData, stackIndex }: { refData: ReviewRef; stackIndex: number }) {
  const accent = chipAccent(refData.source, refData.backend);
  const favicon = resolveFavicon(refData);
  const style: React.CSSProperties = {
    marginLeft: stackIndex === 0 ? 0 : -7,
    zIndex: 10 - stackIndex,
    background: accent.bg,
    borderColor: accent.ring,
  };
  if (favicon) {
    return (
      <span
        className="inline-flex h-[18px] w-[18px] items-center justify-center overflow-hidden rounded-full border bg-white"
        style={style}
        aria-hidden
      >
        <img
          src={favicon}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
          onError={(e) => {
            // Favicon load failure (DDG miss, host returns no icon, etc.)
            // → fall back to the accent emoji. Swap the img DOM for the
            // emoji text so the avatar slot keeps its size and the stack
            // alignment doesn't shift.
            const span = e.currentTarget.parentElement;
            if (span) {
              span.innerHTML = `<span style="font-size:10px">${accent.emoji}</span>`;
            }
          }}
        />
      </span>
    );
  }
  return (
    <span
      className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border text-[10px]"
      style={style}
      aria-hidden
    >
      {accent.emoji}
    </span>
  );
}

function CollapsedSummary({ refs, onExpand }: { refs: ReviewRef[]; onExpand: () => void }) {
  const preview = refs.slice(0, AVATAR_PREVIEW_COUNT);
  const label = `${refs.length} ${refs.length === 1 ? "source" : "sources"}`;
  return (
    <button
      type="button"
      onClick={onExpand}
      title={`Show ${label}`}
      className="inline-flex items-center gap-2 rounded-full border border-border bg-surface-2 px-2 py-1 text-[12px] text-text-sec transition hover:bg-surface-3 hover:text-text"
    >
      <span className="flex items-center">
        {preview.map((r, i) => (
          <SourceAvatar key={r.url} refData={r} stackIndex={i} />
        ))}
      </span>
      <span className="font-medium">{label}</span>
      <span aria-hidden className="text-text-muted">▾</span>
    </button>
  );
}

export function ReviewChip({ refData }: ReviewChipProps) {
  const accent = chipAccent(refData.source, refData.backend);
  // Title is the primary label; source becomes secondary metadata in the
  // tooltip so a hover surfaces the host without bloating the chip.
  const label = truncate(refData.title || refData.source || refData.url, TITLE_TRUNCATE);
  const tooltip = refData.source
    ? `${refData.title}\n${refData.source}`
    : refData.title || refData.url;
  return (
    <a
      href={refData.url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      title={tooltip}
      className="inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11.5px] transition hover:opacity-90"
      style={{
        borderColor: accent.ring,
        background: accent.bg,
        color: accent.text,
      }}
    >
      <SourceAvatar refData={refData} stackIndex={0} />
      <span className="truncate">{label}</span>
      <span aria-hidden className="shrink-0 text-text-muted">↗</span>
    </a>
  );
}

export function ReviewChipRow({ refs }: { refs: ReviewRef[] }) {
  const [expanded, setExpanded] = useState(false);
  if (refs.length === 0) return null;
  if (!expanded) {
    return (
      <div className="mt-2">
        <CollapsedSummary refs={refs} onExpand={() => setExpanded(true)} />
      </div>
    );
  }
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {refs.map((r) => (
        <ReviewChip key={r.url} refData={r} />
      ))}
      <button
        type="button"
        onClick={() => setExpanded(false)}
        className="rounded-full border border-border bg-surface-2 px-2 py-0.5 text-[11px] text-text-sec transition hover:bg-surface-3 hover:text-text"
      >
        Hide
      </button>
    </div>
  );
}
